import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useCurrentUser } from '../context/UserContext';
import { CONNECTOR_SCHEMAS, schemaFor, destinationSummary } from '../utils/deliveryConnectors';
import { readFileHeaderRow, bestFieldMatch, readMappingDocRows, classifyMappingDocRows } from '../utils/fileFieldMatch';
import SearchableSelect from '../components/SearchableSelect';
import CollapsibleSection from '../components/CollapsibleSection';
import ProgramRoutesEditor from '../components/ProgramRoutesEditor';
import { routesFromConfig, routesToConfig, routeProblems } from '../utils/programRoutes';
import { buildStatusGroupMap, buildProgramOptionGroups, closedProgramIdSet, buildComponentOptionGroups, buildGoalOptionGroups } from '../utils/statusGroups';

const API = process.env.REACT_APP_API_URL;

// ---------------------------------------------------------------------------
// Style constants (matches LeadDelivery.js / LeadQA.js conventions)
// ---------------------------------------------------------------------------
const thStyle = {
  padding: '7px 12px', textAlign: 'left', borderBottom: '1px solid #aed6f1',
  fontWeight: '600', color: '#1a6da8', fontSize: '12px', userSelect: 'none',
  whiteSpace: 'nowrap', background: '#eaf4fb',
};
const tdStyle = {
  padding: '7px 12px', borderBottom: '1px solid #eaf2fb', fontSize: '12px', color: '#333',
};
const labelStyle = { fontSize: '12px', color: '#555', marginBottom: '4px', display: 'block', fontWeight: '600' };
const inputStyle = {
  padding: '6px 10px', border: '1px solid #ccc', borderRadius: '4px',
  fontSize: '13px', width: '100%', boxSizing: 'border-box',
};
const btnStyle = (color, disabled) => ({
  padding: '6px 14px', background: disabled ? '#bdc3c7' : color,
  color: 'white', border: 'none', borderRadius: '4px',
  cursor: disabled ? 'default' : 'pointer', fontWeight: '600', fontSize: '12px',
});
const sectionStyle = {
  background: 'white', border: '1px solid #e0e6ed', borderRadius: '8px',
  padding: '18px 20px', marginBottom: '20px',
};
const sectionTitleStyle = { margin: '0 0 12px 0', fontSize: '16px', color: '#2c3e50' };

const CONNECTOR_TYPES = ['convertr', 'marketo', 'integrate'];

// ---------------------------------------------------------------------------
// Connection create/edit form — field set driven by CONNECTOR_SCHEMAS
// ---------------------------------------------------------------------------
// A connection whose Base URL points at a Postman Mock Server — used to badge
// it in the connections table so a test/mock connection is never confused with
// a real client one, regardless of whether it followed the "TEST —" naming
// convention below.
const isMockUrl = (baseUrl) => /\.mock\.pstmn\.io/i.test(baseUrl || '');
const testConnectionName = (connectorType) => `TEST — ${schemaFor(connectorType).label} Mock`;

function ConnectionForm({ initial, clients, onSave, onCancel, testMode }) {
  const [connectorType, setConnectorType] = useState(initial?.connector_type || 'convertr');
  const schema = schemaFor(connectorType);
  const blankCreds = () => Object.fromEntries(schema.credentialFields.map((f) => [f.name, '']));
  const blankExtras = () => Object.fromEntries(
    schema.connectionExtraFields.map((f) => [f.name, initial?.target_defaults?.[f.name] ?? ''])
  );

  const [form, setForm] = useState({
    client_id: initial?.client_id || '',
    name: initial?.name || (testMode ? testConnectionName(connectorType) : ''),
    base_url: initial?.base_url || '',
    active: initial ? initial.active : true,
  });
  const [creds, setCreds] = useState(blankCreds());
  const [extras, setExtras] = useState(blankExtras());

  const onTypeChange = (e) => {
    const next = e.target.value;
    setConnectorType(next);
    setCreds(Object.fromEntries(schemaFor(next).credentialFields.map((f) => [f.name, ''])));
    setExtras(Object.fromEntries(schemaFor(next).connectionExtraFields.map((f) => [f.name, ''])));
    // Keep the auto-generated test name in sync with the chosen type, but only
    // while it still looks auto-generated — never overwrite a name the admin
    // has since typed over.
    if (testMode) {
      setForm((prev) => (
        prev.name === '' || prev.name === testConnectionName(connectorType)
          ? { ...prev, name: testConnectionName(next) }
          : prev
      ));
    }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const setCred = (name) => (e) => setCreds({ ...creds, [name]: e.target.value });
  const setExtra = (name) => (e) => setExtras({ ...extras, [name]: e.target.value });

  const credsFilled = schema.credentialFields.every((f) => creds[f.name]);
  const canSave = form.client_id && form.name && form.base_url && (initial?.has_credentials || credsFilled);

  const save = () => {
    const body = {
      client_id: Number(form.client_id), name: form.name,
      connector_type: connectorType, base_url: form.base_url,
      target_defaults: Object.fromEntries(
        schema.connectionExtraFields.map((f) => [f.name, extras[f.name] ? (f.type === 'number' ? Number(extras[f.name]) : extras[f.name]) : null])
      ),
    };
    if (initial) body.active = form.active;
    // credentials are write-only: only send when every field is (re)entered
    if (credsFilled) body.credentials = creds;
    onSave(body);
  };

  return (
    <div style={{ background: '#f8fafc', border: '1px solid #dde6ee', borderRadius: '6px', padding: '14px', marginTop: '10px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
        <div>
          <label style={labelStyle}>Client</label>
          <select style={inputStyle} value={form.client_id} onChange={set('client_id')}>
            <option value="">— select client —</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Connection Name</label>
          <input style={inputStyle} value={form.name} onChange={set('name')} placeholder={`e.g. Acme ${schema.label}`} />
        </div>
        <div>
          <label style={labelStyle}>Connector Type</label>
          <select style={inputStyle} value={connectorType} onChange={onTypeChange}>
            {CONNECTOR_TYPES.map((t) => <option key={t} value={t}>{CONNECTOR_SCHEMAS[t].label}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Base URL</label>
          <input style={inputStyle} value={form.base_url} onChange={set('base_url')}
                 placeholder={testMode ? 'Paste your Postman Mock Server URL here' : 'https://...'} />
          {testMode && (
            <div style={{ fontSize: '11px', color: '#8e44ad', marginTop: '3px' }}>
              🧪 See <code>docs/runbooks/connector-mock-testing.md</code> for how to turn one of the
              collections in <code>docs/postman/</code> into a mock server URL.
            </div>
          )}
        </div>
        {schema.credentialFields.map((f) => (
          <div key={f.name}>
            <label style={labelStyle}>{f.label} {initial?.has_credentials ? '(set — leave blank to keep)' : ''}</label>
            <input style={inputStyle} type={f.type === 'password' ? 'password' : 'text'} value={creds[f.name]}
                   onChange={setCred(f.name)} autoComplete={f.type === 'password' ? 'new-password' : 'off'}
                   placeholder={testMode && f.name === 'identity_endpoint' ? 'Same mock server URL as Base URL' : f.placeholder} />
          </div>
        ))}
        {schema.connectionExtraFields.map((f) => (
          <div key={f.name}>
            <label style={labelStyle}>{f.label}</label>
            <input style={inputStyle} value={extras[f.name]} onChange={setExtra(f.name)} placeholder={f.placeholder} />
          </div>
        ))}
      </div>
      {initial && (
        <label style={{ ...labelStyle, marginTop: '10px' }}>
          <input type="checkbox" checked={form.active} onChange={set('active')} style={{ marginRight: '6px' }} />
          Active
        </label>
      )}
      <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
        <button style={btnStyle('#2980b9', !canSave)} disabled={!canSave} onClick={save}>
          {initial ? 'Save Changes' : testMode ? 'Create Test Connection' : 'Create Connection'}
        </button>
        <button style={btnStyle('#7f8c8d')} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Destination create/edit form (with Discover helper)
// ---------------------------------------------------------------------------
function MappingEditor({ label, entries, setEntries, keyPlaceholder, valPlaceholder }) {
  const update = (i, which, val) => {
    const next = entries.map((e, j) => (j === i ? { ...e, [which]: val } : e));
    setEntries(next);
  };
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      {entries.map((e, i) => (
        <div key={i} style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
          <input style={{ ...inputStyle, flex: 1 }} value={e.key} placeholder={keyPlaceholder}
                 onChange={(ev) => update(i, 'key', ev.target.value)} />
          <span style={{ alignSelf: 'center', color: '#888' }}>←</span>
          <input style={{ ...inputStyle, flex: 1 }} value={e.value} placeholder={valPlaceholder}
                 onChange={(ev) => update(i, 'value', ev.target.value)} />
          <button style={btnStyle('#c0392b')} onClick={() => setEntries(entries.filter((_, j) => j !== i))}>✕</button>
        </div>
      ))}
      <button style={btnStyle('#16a085')} onClick={() => setEntries([...entries, { key: '', value: '' }])}>
        + Add Row
      </button>
    </div>
  );
}

const entriesToObj = (entries) => Object.fromEntries(
  entries.filter((e) => e.key.trim()).map((e) => [e.key.trim(), e.value])
);
const objToEntries = (obj) => Object.entries(obj || {}).map(([key, value]) => ({ key, value: String(value) }));

// form[firstName] -> firstName; anything else passes through — same transform
// app/routers/lead_delivery.py's _inner_name() applies server-side, so a
// downloaded template's headers read the same whether generated here or there.
const cleanFieldName = (key) => {
  const m = /^form\[(.+)\]$/.exec((key || '').trim());
  return m ? m[1] : (key || '').trim();
};

// Builds a CSV with one header row per mapped destination field (CSV column
// name if set, else the destination field's own name for auto-match entries)
// plus one blank example row, and triggers a browser download — entirely
// client-side, so it works even for an unsaved destination still being drafted.
function downloadLeadTemplate(mapping, filenameBase) {
  const headers = [];
  const seen = new Set();
  mapping.forEach((e) => {
    const col = (e.value || '').trim() || cleanFieldName(e.key);
    if (col && !seen.has(col)) { seen.add(col); headers.push(col); }
  });
  if (!headers.length) return;
  const esc = (v) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const csv = [headers.map(esc).join(','), headers.map(() => '').join(',')].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(filenameBase || 'delivery').replace(/[^a-z0-9]+/gi, '_').toLowerCase()}_lead_template.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// Inline credentials editor for the selected connection (admin only) — lets
// credentials be entered right where the destination is configured. Field set
// comes from the connection's own connector_type via CONNECTOR_SCHEMAS.
function ConnectionCredentials({ connection, onSaved }) {
  const schema = schemaFor(connection.connector_type);
  const [creds, setCreds] = useState(Object.fromEntries(schema.credentialFields.map((f) => [f.name, ''])));
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);   // {ok, text}

  const filled = schema.credentialFields.every((f) => creds[f.name]);

  const save = () => {
    setSaving(true);
    setMsg(null);
    axios.put(`${API}/lead-delivery/connections/${connection.id}`, { credentials: creds })
      .then(() => {
        setMsg({ ok: true, text: 'Credentials saved (encrypted).' });
        setCreds(Object.fromEntries(schema.credentialFields.map((f) => [f.name, ''])));
        onSaved();
      })
      .catch((err) => setMsg({ ok: false, text: err.response?.data?.detail || String(err) }))
      .finally(() => setSaving(false));
  };

  return (
    <div style={{ border: '1px dashed #b7c6d6', borderRadius: '6px', padding: '10px 12px', marginTop: '12px', background: '#fdfefe' }}>
      <label style={labelStyle}>
        {schema.label} Credentials for "{connection.name}" — {connection.has_credentials ? '🔒 currently set (enter below to replace)' : '⚠️ not set yet'}
      </label>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
        {schema.credentialFields.map((f) => (
          <input key={f.name} style={{ ...inputStyle, width: '220px' }} type={f.type === 'password' ? 'password' : 'text'}
                 value={creds[f.name]} placeholder={f.label}
                 autoComplete={f.type === 'password' ? 'new-password' : 'off'}
                 onChange={(e) => setCreds({ ...creds, [f.name]: e.target.value })} />
        ))}
        <button style={btnStyle('#2980b9', !filled || saving)} disabled={!filled || saving} onClick={save}>
          {saving ? 'Saving…' : 'Save Credentials'}
        </button>
        {msg && <span style={{ fontSize: '12px', color: msg.ok ? '#1e8449' : '#c0392b' }}>{msg.text}</span>}
      </div>
    </div>
  );
}

function DestinationForm({ initial, connections, programs, programStatuses, components, goals, destinations, isAdmin, onCredsSaved, onSave, onCancel }) {
  // Closed programs nest into a collapsible section on every Program/
  // Component/Goal picker below instead of mixing into the flat list — see
  // src/utils/statusGroups.js.
  const programStatusGroupMap = buildStatusGroupMap(programStatuses);
  const programOptionGroups = buildProgramOptionGroups(programs, programStatusGroupMap);
  const closedProgramIds = closedProgramIdSet(programs, programStatusGroupMap);
  const componentByIdForGoals = new Map(components.map(c => [c.id, c]));
  const initialConn = connections.find((c) => String(c.id) === String(initial?.connection_id));
  const schemaForFields = (connectorType) => schemaFor(connectorType);
  const blankTargetFields = (connectorType) => Object.fromEntries(
    schemaForFields(connectorType).destinationFields.map((f) => [f.name, initial?.target_config?.[f.name] ?? ''])
  );

  const [form, setForm] = useState({
    name: initial?.name || '',
    connection_id: initial?.connection_id || '',
    program_id: initial?.program_id || '',
    component_id: initial?.component_id || '',
    goal_id: initial?.goal_id || '',
    active: initial ? initial.active : true,
  });
  const [targetFields, setTargetFields] = useState(blankTargetFields(initialConn?.connector_type));
  const [mapping, setMapping] = useState(objToEntries(initial?.field_mapping));
  const [fixed, setFixed] = useState(objToEntries(initial?.fixed_values));
  // Marketo only: per-lead program routing (target_config.program_routes) —
  // kept in its own state because destinationFields (the generic target_config
  // builder in save()) only handles flat text/number/checkbox values.
  const initialRoutes = routesFromConfig(initial?.target_config?.program_routes);
  const [routeField, setRouteField] = useState(initialRoutes.field);
  const [routeRows, setRouteRows] = useState(initialRoutes.rows);
  const [routeError, setRouteError] = useState('');
  const [discovered, setDiscovered] = useState(null);
  const [discoverErr, setDiscoverErr] = useState('');
  const [discovering, setDiscovering] = useState(false);

  const selectedConn = connections.find((c) => String(c.id) === String(form.connection_id));
  const schema = schemaForFields(selectedConn?.connector_type);

  // Sibling destinations already configured on the same connection — same
  // connector_type, so the same target-field names and (usually) a lot of the
  // same field mapping. Excludes this destination itself when editing.
  const siblingDestinations = (destinations || []).filter((d) =>
    form.connection_id && String(d.connection_id) === String(form.connection_id) && d.id !== initial?.id
  );

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const setTarget = (name) => (e) => setTargetFields({ ...targetFields, [name]: e.target.value });
  const onConnectionChange = (e) => {
    const connId = e.target.value;
    const conn = connections.find((c) => String(c.id) === String(connId));
    setForm({ ...form, connection_id: connId });
    setTargetFields(blankTargetFields(conn?.connector_type));
    setDiscovered(null);
    setDiscoverErr('');
  };
  const progComponents = components.filter((c) => String(c.program_id) === String(form.program_id));
  const compGoals = goals.filter((g) => String(g.component_id) === String(form.component_id));
  const progComponentOptionGroups = buildComponentOptionGroups(progComponents, closedProgramIds);
  const compGoalOptionGroups = buildGoalOptionGroups(compGoals, componentByIdForGoals, closedProgramIds);
  const canSave = form.connection_id && form.program_id;

  const discoverEnabled = schema.discoverSupported && form.connection_id &&
    (!schema.discoverNeedsCampaign || targetFields.campaign_id);

  const discover = async () => {
    setDiscovering(true);
    setDiscoverErr('');
    setDiscovered(null);
    try {
      const params = schema.discoverNeedsCampaign ? { campaign_id: targetFields.campaign_id } : {};
      const res = await axios.get(`${API}/lead-delivery/connections/${form.connection_id}/discover`, { params });
      setDiscovered(res.data);
    } catch (err) {
      setDiscoverErr(err.response?.data?.detail || String(err));
    } finally {
      setDiscovering(false);
    }
  };

  const applyForm = (f) => {
    setTargetFields((prev) => ({ ...prev, form_id: f.formId }));
    // Prefill the mapping targets with the form's fields (keep existing values)
    const existing = entriesToObj(mapping);
    const fields = (f.fields || []).map((fd) => (typeof fd === 'string' ? fd : fd.key))
      .filter((k) => k && k !== 'form[submit]');
    setMapping(fields.map((k) => ({ key: k, value: existing[k] || '' })));
  };

  const applyMarketoField = (f) => {
    const existing = entriesToObj(mapping);
    if (f.name in existing) return;
    setMapping([...mapping, { key: f.name, value: '' }]);
  };

  // --- Quick Setup: auto-match the Field Mapping from a file's header row,
  // or from an already-saved Export Config's output columns. Both produce the
  // same thing — an ordered list of candidate CSV column names — so both feed
  // the same matching step: fill in any existing mapping entry's blank CSV
  // column by best-fuzzy-match against those candidates, or (if no target
  // fields are defined yet) seed the mapping fresh from the candidates.
  const [quickMatchErr, setQuickMatchErr] = useState('');
  const [quickMatchMsg, setQuickMatchMsg] = useState('');
  const [quickMatching, setQuickMatching] = useState(false);
  const [progConfigs, setProgConfigs] = useState([]);
  const [configQuery, setConfigQuery] = useState('');
  const [configResults, setConfigResults] = useState([]);
  const [configSearching, setConfigSearching] = useState(false);

  useEffect(() => {
    if (!form.program_id) { setProgConfigs([]); return; }
    axios.get(`${API}/export-configs/`, { params: { program_id: form.program_id } })
      .then((r) => setProgConfigs(r.data || []))
      .catch(() => setProgConfigs([]));
  }, [form.program_id]);

  useEffect(() => {
    if (!configQuery.trim()) { setConfigResults([]); return; }
    setConfigSearching(true);
    const t = setTimeout(() => {
      axios.get(`${API}/export-configs/search`, { params: { q: configQuery.trim() } })
        .then((r) => setConfigResults(r.data || []))
        .catch(() => setConfigResults([]))
        .finally(() => setConfigSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [configQuery]);

  const applyCandidateColumns = (columns, sourceLabel) => {
    if (!columns.length) return;
    let matched = 0;
    setMapping((prev) => {
      if (prev.filter((e) => e.key.trim()).length === 0) {
        // No target fields defined yet — seed the mapping directly from the
        // uploaded/exported column names.
        matched = columns.length;
        return columns.map((c) => ({ key: cleanFieldName(c), value: c }));
      }
      return prev.map((entry) => {
        if (entry.value.trim()) return entry; // don't clobber an already-set column
        const m = bestFieldMatch(cleanFieldName(entry.key), columns.map((c) => ({ key: c, label: c })));
        if (!m) return entry;
        matched += 1;
        return { ...entry, value: m.field.key };
      });
    });
    setQuickMatchMsg(`Matched ${matched} of ${columns.length} columns from ${sourceLabel}.`);
    setQuickMatchErr('');
  };

  const handleQuickFile = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setQuickMatching(true);
    setQuickMatchErr('');
    setQuickMatchMsg('');
    try {
      const headers = await readFileHeaderRow(file);
      if (!headers.length) { setQuickMatchErr('No header row found in that file.'); return; }
      applyCandidateColumns(headers, `"${file.name}"`);
    } catch (err) {
      setQuickMatchErr(err.message || 'Could not read that file — is it a valid CSV or XLSX?');
    } finally {
      setQuickMatching(false);
    }
  };

  const applyExportConfig = (cfg) => {
    // Each selected field's effective output column: its custom export label
    // if the config renamed it, else the raw NeTrA field key.
    const columns = (cfg.selected_fields || []).map((k) => (cfg.field_mapping || {})[k] || k);
    applyCandidateColumns(columns, `the "${cfg.name}" export config`);
    setConfigQuery('');
    setConfigResults([]);
  };

  // Clones another destination's field mapping, fixed values, and target config
  // wholesale — for the common case of a second program on the same client's
  // connection, where most of the field mapping is identical and only a few
  // per-program specifics (campaign ID, UTM values) need to change afterward.
  const applyDestination = (d) => {
    setMapping(objToEntries(d.field_mapping));
    setFixed(objToEntries(d.fixed_values));
    setTargetFields((prev) => ({ ...prev, ...(d.target_config || {}) }));
    const copiedRoutes = routesFromConfig(d.target_config?.program_routes);
    setRouteField(copiedRoutes.field);
    setRouteRows(copiedRoutes.rows);
    setQuickMatchMsg(`Copied ${Object.keys(d.field_mapping || {}).length} mapped fields, ${Object.keys(d.fixed_values || {}).length} fixed values, and target config from "${d.name}" — adjust program-specific values below.`);
    setQuickMatchErr('');
  };

  // A client-provided mapping doc (target field / source column / notes / literal
  // value, one row per field) already spells out both sides of the mapping
  // explicitly — no fuzzy-matching needed, so this replaces Field Mapping and
  // Fixed Values outright rather than merging into applyCandidateColumns()'s
  // blank-value-only matching.
  const handleMappingDocFile = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setQuickMatching(true);
    setQuickMatchErr('');
    setQuickMatchMsg('');
    try {
      const rows = await readMappingDocRows(file);
      if (!rows.length) { setQuickMatchErr('No rows found in that mapping document.'); return; }
      const { mapping: mapRows, fixed: fixedRows, pending } = classifyMappingDocRows(rows);
      if (!mapRows.length && !fixedRows.length) {
        setQuickMatchErr('Could not find any usable rows — expected 4 columns: friendly label, target field, notes, value.');
        return;
      }
      setMapping(mapRows);
      setFixed(fixedRows);
      const parts = [`Imported ${mapRows.length} mapped field${mapRows.length === 1 ? '' : 's'}`];
      if (fixedRows.length) parts.push(`${fixedRows.length} set as fixed value${fixedRows.length === 1 ? '' : 's'}`);
      if (pending.length) parts.push(`${pending.length} pending client confirmation (${pending.map((p) => p.label || p.key).join(', ')}) — left unmapped`);
      setQuickMatchMsg(`${parts.join(', ')} from "${file.name}".`);
    } catch (err) {
      setQuickMatchErr(err.message || 'Could not read that mapping document — is it a valid CSV or XLSX?');
    } finally {
      setQuickMatching(false);
    }
  };

  const save = () => {
    const target_config = Object.fromEntries(
      schema.destinationFields.map((f) => {
        if (f.type === 'checkbox') return [f.name, !!targetFields[f.name]];
        return [f.name, targetFields[f.name] ? (f.type === 'number' ? Number(targetFields[f.name]) : targetFields[f.name]) : null];
      })
    );
    if (selectedConn?.connector_type === 'convertr') {
      // publisher_id lives on the connection now; keep a legacy per-destination
      // value only if this destination already had one
      target_config.publisher_id = initial?.target_config?.publisher_id || null;
    }
    if (selectedConn?.connector_type === 'marketo') {
      const problems = routeProblems(routeField, routeRows);
      if (problems.length) {
        setRouteError(problems.join(' '));
        return;
      }
      setRouteError('');
      // null clears routing (the single Program Name above applies again)
      target_config.program_routes = routesToConfig(routeField, routeRows);
    }
    onSave({
      name: form.name || null,
      connection_id: Number(form.connection_id),
      program_id: Number(form.program_id),
      component_id: form.component_id ? Number(form.component_id) : null,
      goal_id: form.goal_id ? Number(form.goal_id) : null,
      target_config,
      field_mapping: entriesToObj(mapping),
      fixed_values: entriesToObj(fixed),
      ...(initial ? { active: form.active } : {}),
    });
  };

  return (
    <div style={{ background: '#f8fafc', border: '1px solid #dde6ee', borderRadius: '6px', padding: '14px', marginTop: '10px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px' }}>
        <div style={{ gridColumn: '1 / span 2' }}>
          <label style={labelStyle}>Destination Name (how you'll recognize it when delivering)</label>
          <input style={inputStyle} value={form.name} onChange={set('name')}
                 placeholder="e.g. Acme — Q3 Webinar Campaign (46689)" />
        </div>
        <div>
          <label style={labelStyle}>Connection</label>
          <select style={inputStyle} value={form.connection_id} onChange={onConnectionChange}>
            <option value="">— select —</option>
            {connections.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Program</label>
          <SearchableSelect value={form.program_id}
            onChange={(v) => setForm({ ...form, program_id: v, component_id: '', goal_id: '' })}
            options={programOptionGroups.options} closedOptions={programOptionGroups.closedOptions}
            placeholder="— select —" />
        </div>
        <div>
          <label style={labelStyle}>Component (optional)</label>
          <SearchableSelect value={form.component_id}
            onChange={(v) => setForm({ ...form, component_id: v, goal_id: '' })}
            options={progComponentOptionGroups.options}
            closedOptions={progComponentOptionGroups.closedOptions}
            placeholder="— all —" />
        </div>
        <div>
          <label style={labelStyle}>Goal (optional)</label>
          <SearchableSelect value={form.goal_id}
            onChange={(v) => setForm({ ...form, goal_id: v })}
            options={compGoalOptionGroups.options}
            closedOptions={compGoalOptionGroups.closedOptions}
            placeholder="— all —" />
        </div>
        {selectedConn?.connector_type === 'convertr' && (
          <div>
            <label style={labelStyle}>Publisher ID</label>
            <div style={{ ...inputStyle, background: '#f0f3f6', color: '#555' }}>
              {(() => {
                const inherited = selectedConn?.target_defaults?.publisher_id;
                const legacy = initial?.target_config?.publisher_id;
                if (legacy) return `${legacy} (set on this destination)`;
                if (inherited) return `${inherited} (from connection)`;
                return '⚠ not set — edit the connection';
              })()}
            </div>
          </div>
        )}
        {schema.destinationFields.map((f) => (
          <div key={f.name}>
            <label style={labelStyle}>{f.label}</label>
            {f.type === 'checkbox' ? (
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', height: '36px' }}>
                <input
                  type="checkbox"
                  checked={!!targetFields[f.name]}
                  onChange={(e) => setTargetFields({ ...targetFields, [f.name]: e.target.checked })}
                />
              </label>
            ) : (
              <input style={inputStyle} value={targetFields[f.name] || ''} onChange={setTarget(f.name)} placeholder={f.placeholder} />
            )}
          </div>
        ))}
      </div>

      {/* Collapsed by default — busy, and rarely needed (only a client that
          sends leads to different programs by value, e.g. by asset title). */}
      {selectedConn?.connector_type === 'marketo' && (
        <CollapsibleSection
          label="Route to a different program by value (optional)"
          count={routeRows.filter((r) => r.title.trim()).length || null}
        >
          <ProgramRoutesEditor
            field={routeField}
            onFieldChange={setRouteField}
            fieldOptions={mapping.map((e) => e.key.trim()).filter((k) => k)}
            rows={routeRows}
            onRowsChange={setRouteRows}
            onLookup={(programId) => axios
              .get(`${API}/lead-delivery/connections/${selectedConn.id}/marketo-program/${programId}`)
              .then((res) => res.data.name)}
          />
        </CollapsibleSection>
      )}
      {selectedConn?.connector_type === 'marketo' && routeError && (
        <div style={{ fontSize: '12px', color: '#c0392b', marginTop: '6px' }}>{routeError}</div>
      )}

      {isAdmin && selectedConn && <ConnectionCredentials connection={selectedConn} onSaved={onCredsSaved} />}

      {schema.discoverSupported ? (
        <div style={{ marginTop: '12px' }}>
          <button style={btnStyle('#8e44ad', !discoverEnabled || discovering)}
                  disabled={!discoverEnabled || discovering} onClick={discover}>
            {discovering ? 'Discovering…' : schema.discoverLabel}
          </button>
          {discoverErr && <span style={{ color: '#c0392b', fontSize: '12px', marginLeft: '10px' }}>{discoverErr}</span>}
        </div>
      ) : form.connection_id && (
        <div style={{ marginTop: '12px' }}>
          <button style={btnStyle('#bdc3c7', true)} disabled title="This connector has no field-discovery endpoint — enter field names manually to match your Source's configuration.">
            🔍 Discover (not available)
          </button>
        </div>
      )}

      {discovered && selectedConn?.connector_type === 'convertr' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
          <div>
            <label style={labelStyle}>Forms on campaign {targetFields.campaign_id} (click to use + prefill mapping)</label>
            {(discovered.forms || []).map((f) => (
              <div key={f.formId}
                   onClick={() => applyForm(f)}
                   style={{
                     border: `1px solid ${String(targetFields.form_id) === String(f.formId) ? '#8e44ad' : '#ddd'}`,
                     borderRadius: '4px', padding: '8px', marginBottom: '6px', cursor: 'pointer', fontSize: '12px',
                     background: String(targetFields.form_id) === String(f.formId) ? '#f5eef8' : 'white',
                   }}>
                <strong>#{f.formId}</strong> {f.formName}
                <div style={{ color: '#888', marginTop: '4px' }}>
                  {(f.fields || []).map((fd) => (typeof fd === 'string' ? fd : fd.key)).join(', ')}
                </div>
              </div>
            ))}
          </div>
          <div>
            <label style={labelStyle}>Links (click to use)</label>
            {(discovered.links || []).map((l) => (
              <div key={l.id}
                   onClick={() => setTargetFields((prev) => ({ ...prev, link_id: l.id }))}
                   style={{
                     border: `1px solid ${String(targetFields.link_id) === String(l.id) ? '#8e44ad' : '#ddd'}`,
                     borderRadius: '4px', padding: '8px', marginBottom: '6px', cursor: 'pointer', fontSize: '12px',
                     background: String(targetFields.link_id) === String(l.id) ? '#f5eef8' : 'white',
                   }}>
                <strong>#{l.id}</strong> {l.name} <span style={{ color: '#888' }}>({l.channel})</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {discovered && selectedConn?.connector_type === 'marketo' && (
        <div style={{ marginTop: '12px' }}>
          <label style={labelStyle}>Lead fields (click to add to Field Mapping below)</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {(discovered.fields || []).map((f) => (
              <div key={f.name} onClick={() => applyMarketoField(f)}
                   title={f.type}
                   style={{
                     border: '1px solid #ddd', borderRadius: '4px', padding: '5px 9px',
                     cursor: 'pointer', fontSize: '12px', background: 'white',
                   }}>
                <strong>{f.name}</strong> <span style={{ color: '#888' }}>({f.label})</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ border: '1px dashed #b7c6d6', borderRadius: '6px', padding: '10px 12px', marginTop: '14px', background: '#fdfefe' }}>
        <label style={labelStyle}>Quick Setup — fill in the Field Mapping's CSV columns automatically</label>
        <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div>
            <label style={{ ...labelStyle, fontWeight: '400', fontSize: '11px' }}>From a sample file</label>
            <br />
            <input type="file" accept=".csv,.xlsx,.xls" disabled={quickMatching} onChange={handleQuickFile} style={{ fontSize: '12px' }} />
          </div>
          <div style={{ flex: 1, minWidth: '260px' }}>
            <label style={{ ...labelStyle, fontWeight: '400', fontSize: '11px' }}>From a saved Export Config</label>
            {!form.program_id ? (
              <div style={{ fontSize: '12px', color: '#999' }}>Pick a Program above first.</div>
            ) : (
              <>
                {progConfigs.length > 0 && (
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '6px' }}>
                    {progConfigs.map((cfg) => (
                      <span key={cfg.id} onClick={() => applyExportConfig(cfg)}
                            style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '3px 8px', fontSize: '12px', cursor: 'pointer', background: 'white' }}>
                        {cfg.name}
                      </span>
                    ))}
                  </div>
                )}
                <input style={{ ...inputStyle, fontSize: '12px' }} placeholder="Search other programs' configs by name…"
                       value={configQuery} onChange={(e) => setConfigQuery(e.target.value)} />
                {configSearching && <div style={{ fontSize: '11px', color: '#999' }}>Searching…</div>}
                {configResults.length > 0 && (
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
                    {configResults.map((cfg) => (
                      <span key={cfg.id} onClick={() => applyExportConfig(cfg)}
                            title={cfg.program_name}
                            style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '3px 8px', fontSize: '12px', cursor: 'pointer', background: 'white' }}>
                        {cfg.name} <span style={{ color: '#999' }}>({cfg.program_name})</span>
                      </span>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          <div>
            <label style={{ ...labelStyle, fontWeight: '400', fontSize: '11px' }}>From a mapping document</label>
            <br />
            <input type="file" accept=".csv,.xlsx,.xls" disabled={quickMatching} onChange={handleMappingDocFile} style={{ fontSize: '12px' }} />
            <div style={{ fontSize: '10px', color: '#999', marginTop: '2px', maxWidth: '190px' }}>
              A client-provided doc (label / target field / notes / value columns) — replaces Field Mapping and Fixed Values below.
            </div>
          </div>
          <div style={{ flex: 1, minWidth: '220px' }}>
            <label style={{ ...labelStyle, fontWeight: '400', fontSize: '11px' }}>From another destination on this connection</label>
            {!form.connection_id ? (
              <div style={{ fontSize: '12px', color: '#999' }}>Pick a Connection above first.</div>
            ) : siblingDestinations.length === 0 ? (
              <div style={{ fontSize: '12px', color: '#999' }}>No other destinations on this connection yet.</div>
            ) : (
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {siblingDestinations.map((d) => (
                  <span key={d.id} onClick={() => applyDestination(d)}
                        title={`${Object.keys(d.field_mapping || {}).length} mapped fields`}
                        style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '3px 8px', fontSize: '12px', cursor: 'pointer', background: 'white' }}>
                    {d.name}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        {quickMatching && <div style={{ fontSize: '12px', color: '#999', marginTop: '6px' }}>Reading file…</div>}
        {quickMatchMsg && <div style={{ fontSize: '12px', color: '#1e8449', marginTop: '6px' }}>{quickMatchMsg}</div>}
        {quickMatchErr && <div style={{ fontSize: '12px', color: '#c0392b', marginTop: '6px' }}>{quickMatchErr}</div>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '14px' }}>
        <MappingEditor label="Field Mapping (target field ← CSV column; leave the CSV column blank to auto-match the header)"
                       entries={mapping} setEntries={setMapping}
                       keyPlaceholder={schema.mappingPlaceholder.key} valPlaceholder={schema.mappingPlaceholder.value} />
        <MappingEditor label="Fixed Values (target field ← literal)" entries={fixed} setEntries={setFixed}
                       keyPlaceholder={schema.fixedPlaceholder.key} valPlaceholder={schema.fixedPlaceholder.value} />
      </div>

      <div style={{ marginTop: '10px' }}>
        <button
          style={btnStyle('#1a6da8', mapping.filter((e) => e.key.trim()).length === 0)}
          disabled={mapping.filter((e) => e.key.trim()).length === 0}
          onClick={() => downloadLeadTemplate(mapping, form.name)}
          title="Downloads a CSV with a header row matching the Field Mapping above, ready to fill in and release."
        >
          ⬇ Download Lead Template
        </button>
      </div>

      {initial && (
        <label style={{ ...labelStyle, marginTop: '10px' }}>
          <input type="checkbox" checked={form.active} onChange={set('active')} style={{ marginRight: '6px' }} />
          Active
        </label>
      )}
      <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
        <button style={btnStyle('#2980b9', !canSave)} disabled={!canSave} onClick={save}>
          {initial ? 'Save Changes' : 'Create Destination'}
        </button>
        <button style={btnStyle('#7f8c8d')} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page — admin setup for connections & destinations
// ---------------------------------------------------------------------------
export default function LeadDeliverySetup() {
  const { currentUser } = useCurrentUser();
  const isAdmin = currentUser?.role === 'admin';

  const [connections, setConnections] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [clients, setClients] = useState([]);
  const [programs, setPrograms] = useState([]);
  // Closed programs nest into a collapsible section on the Program picker
  // below instead of mixing into the flat list — see src/utils/statusGroups.js.
  const [programStatuses, setProgramStatuses] = useState([]);
  const [components, setComponents] = useState([]);
  const [goals, setGoals] = useState([]);

  const [editingConn, setEditingConn] = useState(null);     // null | 'new' | 'new-test' | connection
  const [editingDest, setEditingDest] = useState(null);
  const [testResults, setTestResults] = useState({});       // connection_id -> {ok|error}
  const [error, setError] = useState('');

  const loadConfig = useCallback(() => {
    axios.get(`${API}/lead-delivery/connections`).then((r) => setConnections(r.data)).catch(() => {});
    axios.get(`${API}/lead-delivery/destinations`).then((r) => setDestinations(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadConfig();
    axios.get(`${API}/programs/`).then((r) => setPrograms(r.data)).catch(() => {});
    axios.get(`${API}/components/`).then((r) => setComponents(r.data)).catch(() => {});
    axios.get(`${API}/goals/`).then((r) => setGoals(r.data)).catch(() => {});
    axios.get(`${API}/clients/`).then((r) => setClients(r.data)).catch(() => {});
    axios.get(`${API}/lookup-values/?category=program_status`).then((r) => setProgramStatuses(r.data)).catch(() => {});
  }, [loadConfig]);

  const apiErr = (err) => setError(err.response?.data?.detail || String(err));

  const saveConnection = (body) => {
    setError('');
    const req = (editingConn === 'new' || editingConn === 'new-test')
      ? axios.post(`${API}/lead-delivery/connections`, body)
      : axios.put(`${API}/lead-delivery/connections/${editingConn.id}`, body);
    req.then(() => { setEditingConn(null); loadConfig(); }).catch(apiErr);
  };

  const deleteConnection = (c) => {
    if (!window.confirm(`Delete connection "${c.name}" and all its destinations?`)) return;
    axios.delete(`${API}/lead-delivery/connections/${c.id}`).then(loadConfig).catch(apiErr);
  };

  const testConnection = (c) => {
    setTestResults((prev) => ({ ...prev, [c.id]: { testing: true } }));
    axios.post(`${API}/lead-delivery/connections/${c.id}/test`)
      .then(() => setTestResults((prev) => ({ ...prev, [c.id]: { ok: true } })))
      .catch((err) => setTestResults((prev) => ({
        ...prev, [c.id]: { error: err.response?.data?.detail || String(err) },
      })));
  };

  const saveDestination = (body) => {
    setError('');
    const req = editingDest === 'new'
      ? axios.post(`${API}/lead-delivery/destinations`, body)
      : axios.put(`${API}/lead-delivery/destinations/${editingDest.id}`, body);
    req.then(() => { setEditingDest(null); loadConfig(); }).catch(apiErr);
  };

  const deleteDestination = (d) => {
    if (!window.confirm('Delete this destination?')) return;
    axios.delete(`${API}/lead-delivery/destinations/${d.id}`).then(loadConfig).catch(apiErr);
  };

  const clientName = (id) => clients.find((c) => c.id === id)?.name || `client #${id}`;
  const programName = (id) => programs.find((p) => p.id === id)?.program_name || `program #${id}`;
  const scopeLabel = (d) => {
    const parts = [programName(d.program_id)];
    if (d.component_id) parts.push(components.find((c) => c.id === d.component_id)?.component_name || `component #${d.component_id}`);
    if (d.goal_id) parts.push(goals.find((g) => g.id === d.goal_id)?.goal_name || `goal #${d.goal_id}`);
    return `${parts.join(' › ')} → ${d.connection_name}`;
  };

  // Destinations for a closed program nest into a "Closed programs" section
  // below the active ones instead of mixing in — same status-group split as
  // Programs/Components/Goals/PM Home/Supplier Portal (src/utils/statusGroups.js).
  // A duplicate-name mixup on a stale/closed program's destination is exactly
  // what caused a real production incident (2026-09-16, Everpure) — the two
  // looked like the same live destination sitting side by side in one flat
  // list, when one actually belonged to a closed program.
  const destinationProgramStatusGroupMap = buildStatusGroupMap(programStatuses);
  const closedDestinationProgramIds = closedProgramIdSet(programs, destinationProgramStatusGroupMap);
  const activeDestinations = destinations.filter((d) => !closedDestinationProgramIds.has(d.program_id));
  const closedDestinations = destinations.filter((d) => closedDestinationProgramIds.has(d.program_id));

  const renderDestinationRow = (d) => (
    <tr key={d.id}>
      <td style={tdStyle}><strong>{d.name || <span style={{ color: '#b7950b' }}>unnamed — click Edit to name it</span>}</strong></td>
      <td style={tdStyle}>{scopeLabel(d)}</td>
      <td style={tdStyle}>{d.connection_name} <span style={{ color: '#888' }}>({d.connector_type})</span></td>
      <td style={tdStyle}>{destinationSummary(d)}</td>
      <td style={tdStyle}>{Object.keys(d.field_mapping || {}).length} fields</td>
      <td style={tdStyle}>{d.active ? '✓' : '—'}</td>
      <td style={tdStyle}>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button style={btnStyle('#2980b9')} onClick={() => setEditingDest(d)}>Edit</button>
          <button style={btnStyle('#c0392b')} onClick={() => deleteDestination(d)}>Delete</button>
        </div>
      </td>
    </tr>
  );

  const renderDestinationsTable = (rows, emptyText) => (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead><tr>
        <th style={thStyle}>Name</th><th style={thStyle}>Scope</th><th style={thStyle}>Connection</th><th style={thStyle}>Target</th>
        <th style={thStyle}>Mapped Fields</th><th style={thStyle}>Active</th><th style={thStyle}>Actions</th>
      </tr></thead>
      <tbody>
        {rows.map(renderDestinationRow)}
        {rows.length === 0 && (
          <tr><td style={tdStyle} colSpan={7}>{emptyText}</td></tr>
        )}
      </tbody>
    </table>
  );

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto', fontFamily: 'Arial, sans-serif' }}>
      <h1 style={{ fontSize: '22px', color: '#2c3e50', marginBottom: '4px' }}>Delivery Setup</h1>
      <p style={{ color: '#7f8c8d', fontSize: '13px', marginTop: 0 }}>
        Configure API connections (credentials, publisher) and destinations (campaign routing
        + field mapping). PMs deliver leads from the Client Leads Delivery page.
      </p>
      {error && (
        <div style={{ background: '#fdedec', border: '1px solid #f5b7b1', color: '#c0392b',
                      borderRadius: '6px', padding: '10px 14px', marginBottom: '14px', fontSize: '13px' }}>
          {error}
        </div>
      )}

      {/* ── 1. Connections ─────────────────────────────────────────────── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>1 · API Connections</h2>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={thStyle}>Name</th><th style={thStyle}>Client</th><th style={thStyle}>Type</th>
            <th style={thStyle}>Base URL</th><th style={thStyle}>Credentials</th><th style={thStyle}>Active</th>
            <th style={thStyle}>Actions</th>
          </tr></thead>
          <tbody>
            {connections.map((c) => (
              <tr key={c.id}>
                <td style={tdStyle}>
                  <strong>{c.name}</strong>
                  {isMockUrl(c.base_url) && (
                    <span title="Base URL points at a Postman Mock Server — not a real client instance"
                          style={{
                            marginLeft: '6px', fontSize: '11px', background: '#f5eef8', color: '#8e44ad',
                            border: '1px solid #d9c2e8', borderRadius: '10px', padding: '1px 7px',
                          }}>
                      🧪 Mock
                    </span>
                  )}
                </td>
                <td style={tdStyle}>{clientName(c.client_id)}</td>
                <td style={tdStyle}>{c.connector_type}</td>
                <td style={tdStyle}>{c.base_url}</td>
                <td style={tdStyle}>{c.has_credentials ? '🔒 set' : '⚠️ missing'}</td>
                <td style={tdStyle}>{c.active ? '✓' : '—'}</td>
                <td style={tdStyle}>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <button style={btnStyle('#16a085', testResults[c.id]?.testing)} onClick={() => testConnection(c)}>
                      {testResults[c.id]?.testing ? 'Testing…' : 'Test'}
                    </button>
                    {isAdmin && <button style={btnStyle('#2980b9')} onClick={() => setEditingConn(c)}>Edit</button>}
                    {isAdmin && <button style={btnStyle('#c0392b')} onClick={() => deleteConnection(c)}>Delete</button>}
                    {testResults[c.id]?.ok && <span style={{ color: '#1e8449', fontSize: '12px' }}>✓ connected</span>}
                    {testResults[c.id]?.error && <span style={{ color: '#c0392b', fontSize: '12px' }}>{testResults[c.id].error}</span>}
                  </div>
                </td>
              </tr>
            ))}
            {connections.length === 0 && (
              <tr><td style={tdStyle} colSpan={7}>No connections yet.</td></tr>
            )}
          </tbody>
        </table>
        {isAdmin && editingConn === null && (
          <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
            <button style={btnStyle('#2980b9')} onClick={() => setEditingConn('new')}>
              + New Connection
            </button>
            <button style={btnStyle('#8e44ad')} onClick={() => setEditingConn('new-test')}
                    title="Create a connection pointed at a Postman Mock Server, to test a connector without touching a real client's instance">
              🧪 New Test Connection
            </button>
          </div>
        )}
        {editingConn !== null && (
          <ConnectionForm initial={editingConn === 'new' || editingConn === 'new-test' ? null : editingConn}
                          clients={clients} testMode={editingConn === 'new-test'}
                          onSave={saveConnection} onCancel={() => setEditingConn(null)} />
        )}
      </div>

      {/* ── 2. Destinations ────────────────────────────────────────────── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>2 · Destinations (program → connection routing & field mapping)</h2>
        {renderDestinationsTable(activeDestinations, 'No destinations yet — create a connection first, then add one.')}
        {closedDestinations.length > 0 && (
          <CollapsibleSection label="Closed programs" count={closedDestinations.length}>
            {renderDestinationsTable(closedDestinations, 'No closed-program destinations.')}
          </CollapsibleSection>
        )}
        {editingDest === null && (
          <button style={{ ...btnStyle('#2980b9'), marginTop: '10px' }}
                  disabled={connections.length === 0}
                  onClick={() => setEditingDest('new')}>
            + New Destination
          </button>
        )}
        {editingDest !== null && (
          <DestinationForm initial={editingDest === 'new' ? null : editingDest}
                           connections={connections} programs={programs} programStatuses={programStatuses}
                           components={components} goals={goals}
                           destinations={destinations}
                           isAdmin={isAdmin} onCredsSaved={loadConfig}
                           onSave={saveDestination} onCancel={() => setEditingDest(null)} />
        )}
      </div>

    </div>
  );
}
